import React, {PureComponent} from "react";
import {QuestionSceneProps} from "./QuestionSceneProps";
import styles from "./question.module.css";
import getFontSize from "../../shared/utils/getFontSize";
import {Random} from "../../shared/utils/random";
import {BonusType} from "../../store/player/types";
import {cssHelper} from "../../shared/utils/cssHelper";
import {BottomPanel} from "../../shared/components/bottom-panel/BottomPanel";
import {iif} from "../../shared/components/iif";
import bottomPanelStyles from "../../shared/components/bottom-panel/bottom-panel.module.css";
import icons from "../../shared/styles/icons.module.css";
import scrollbar from "../../shared/styles/scrollbars.module.css";
import {getQuestionHintsId} from "../../store/hints/types";
import getShareImage from "../../shared/utils/getShareImage";
import interpolate from "../../shared/utils/interpolate";
import renderText from "../../shared/components/renderText";
import HintPanel from "../../shared/components/hint-panel/HintPanel";
import toDataURL from "../../shared/utils/toDataURL";
import {LogLevel} from "../../store/log/types";
import Comments, {ICommentsWidget} from "../../shared/components/comments/Comments";
import playSound from "../../shared/sounds/playSound";
import {Feedback} from "../../store/questions/types";
import i18n from "../../shared/i18n";
import { SDK } from "../../sdk";
import isTouchDevice from "../../shared/utils/isTouchDevice";

const SHOW_SHARE_TUTOR_ANSWER_THRESHOLD = 10;

let questionAnswers = 0;
let tutorShown = false;

export class QuestionSceneBase<TState> extends PureComponent<QuestionSceneProps, TState> {
    protected readonly hostElement: React.RefObject<HTMLElement> = React.createRef();
    private readonly scrollView: React.RefObject<HTMLDivElement> = React.createRef();
    private readonly dialogContainer: React.RefObject<HTMLDivElement> = React.createRef();
    private readonly comments: React.RefObject<ICommentsWidget> = React.createRef();

    componentDidMount(): void {
        this.props.showQuestion(this.props.question.id);
    }

    componentDidUpdate(prevProps: Readonly<QuestionSceneProps>, prevState: Readonly<TState>, snapshot?: any): void {
        if (this.props.question.id !== prevProps.question.id) {
            this.props.showQuestion(this.props.question.id);
        }

        if (!SDK.instance.supports.shares)
            return;

        if (this.props.isAnswered && !prevProps.isAnswered && !tutorShown) {
            questionAnswers++;

            if (this.props.progress.isRight && questionAnswers >= SHOW_SHARE_TUTOR_ANSWER_THRESHOLD) {
                tutorShown = true;

                this.props.showTutorial({
                    selector: `.${styles.share_button}`,
                    title: "questionanswer.sharetutor",
                    delay: this.props.animation.enabled ? 1.0 : 0,
                    closeOnTouch: true,
                    propagateEvents: true,
                });
            }
        }
    }

    render() {
        const {question} = this.props;
        const timer = !this.props.isAnswered
            ? <div className={styles.timer}/>
            : undefined;
        const answers = this.renderAnswers();
        const explanation = this.renderExplanation();
        const bottomPanel = this.renderBottomPanel();
        const comments = this.renderComments();

        return (
            <section className={styles.container} ref={this.hostElement}>
                {timer}
                <div className={cssHelper(styles.scroll_view).add(isTouchDevice() ? scrollbar.empty : scrollbar.thin).toString()} ref={this.scrollView}>
                    <figure className={styles.image}>
                        <img src={question.imageUrl} alt={question.title}/>
                    </figure>

                    <header className={styles.text} style={{ fontSize: getFontSize(question.title.length, 36, 44, 60, 190) }}>{question.title}</header>

                    <div className={styles.answers}>
                        {answers}
                    </div>

                    {explanation}

                    {comments}
                </div>

                {bottomPanel}

                <div ref={this.dialogContainer}/>
            </section>
        );
    }

    private renderAnswers() {
        const {isAnswered, isTimeoutExpired, question, answers, progress, appliedHints, randomSeed} = this.props;

        const random = new Random(randomSeed);
        const fontSize = Math.min(...question.answers.map(answerId => getFontSize(answers[answerId].text.length, 30, 34, 34, 50)));
        const wrongAnswers = question.answers.filter(answerId => !answers[answerId].isRight);
        const disabledAnswers = appliedHints.includes(BonusType.FiftyFifty)
            ? random.shuffle(wrongAnswers).slice(0, Math.floor(question.answers.length / 2))
            : [];

        return question.answers.map((answerId => {
            const answer = answers[answerId];
            const className = cssHelper(styles.answer)
                .add(styles.right_answer, isAnswered && !!answer.isRight && !isTimeoutExpired)
                .add(styles.answer_timeout, isAnswered && !!answer.isRight && isTimeoutExpired)
                .add(styles.wrong_answer, !!progress && progress.answerId === answerId && !answer.isRight)
                .add(styles.selected_answer, !!progress && progress.answerId === answerId);

            let stats;
            const showStats = isAnswered || appliedHints.includes(BonusType.MajorityVote);
            if (showStats) {
                const answerStats = question.answerStats.stats.find(answer => answer.answerId === answerId) || {count: 0};
                stats = (<i className={styles.stats} style={{ width: `${(answerStats.count / question.answerStats.totalAnswerCount * 100).toFixed(1)}%` }}/>);
            }

            return (
                <button key={answerId} className={className.toString()} onClick={this.getAnswerHandler(answerId, isAnswered)} style={{ fontSize }} disabled={disabledAnswers.includes(answerId)}>
                    <span>{answer.text}</span>
                    {stats}
                </button>
            );
        }));
    }

    private renderBottomPanel() {
        const {isAnswered, progress, animation} = this.props;

        if (!isAnswered) {
            return this.renderHintPanel();
        }

        const showFeedbackControls = !progress.feedback && !!progress.answerId;

        return (
            <BottomPanel delay={0.5} animation={animation}>
                {iif(SDK.instance.supports.shares,
                    <div className={styles.share_button_container}>
                        <button className={cssHelper(bottomPanelStyles.button, styles.share_button).toString()} onClick={() => this.share()}>
                            <i className={cssHelper(icons.icon, icons.share).toString()}/>
                            <span>{i18n("questionanswer.buttonsblock.sharebutton")}</span>
                        </button>
                    </div>
                )}

                <button className={cssHelper(bottomPanelStyles.button, styles.good_button).add(styles.feedback_buttons_disabled, !showFeedbackControls).toString()} onClick={() => this.leaveFeedback(Feedback.Like)}>
                    <i className={cssHelper(icons.icon, icons.like).toString()}/>
                </button>

                <button className={cssHelper(bottomPanelStyles.button, styles.bad_button).add(styles.feedback_buttons_disabled, !showFeedbackControls).toString()} onClick={() => this.leaveFeedback(Feedback.Dislike)}>
                    <i className={cssHelper(icons.icon, icons.dislike).toString()}/>
                </button>

                <button className={cssHelper(bottomPanelStyles.button, styles.comment_button).add(styles.feedback_buttons_disabled, showFeedbackControls).toString()} onClick={() => this.comments.current && this.comments.current.writeComment()}>
                    <span>{i18n("questionanswer.buttonsblock.commentbutton")}</span>
                </button>

                <button className={cssHelper(bottomPanelStyles.button, styles.continue_button).toString()} onClick={() => this.continue()}>
                    <span>{this.props.continueButtonText || i18n("questionanswer.buttonsblock.continuebutton")}</span>
                </button>
            </BottomPanel>
        );
    }

    private renderHintPanel() {
        const {question} = this.props;

        return (
            <HintPanel animate={true}
                       delay={0.5}
                       id={getQuestionHintsId(question.id)}
                       caption={i18n("questionanswer.hints.header")}
                       hints={[BonusType.DoubleChance, BonusType.FiftyFifty, BonusType.MajorityVote, BonusType.SkipQuestion]}/>
        );
    }

    private renderExplanation() {
        const {question, authors, isAnswered} = this.props;

        if (!isAnswered)
            return;

        const author = question.author && authors[question.author]
            ? renderText(authors[question.author].name, 28, 36, 35, 50, styles.author)
            : undefined;

        let source;
        if (question.source && question.source.show) {
            source = i18n("questionanswer.sourceprefix") + question.source.title;
            const fontSize = getFontSize(source.length, 22, 30, 45, 70);

            if (question.source.showUrl) {
                source = (<a href={question.source.url} target="_blank" rel="noopener noreferrer" className={styles.source} style={{fontSize: fontSize}}>{source}</a>);
            } else {
                source = (<div className={styles.source} style={{fontSize: fontSize}}>{source}</div>);
            }
        }

        return (
            <div className={styles.explanation}>
                {renderText(question.description, 32, 36, 120, 540)}
                {author}
                {source}
            </div>
        );
    }

    private renderComments() {
        if (!this.props.isAnswered)
            return;

        return (<Comments ref={this.comments as any} url={`posts/${this.props.question.id}`} questionId={this.props.question.id} scrollView={this.scrollView} dialogContainer={this.dialogContainer}/>);
    }

    private getAnswerHandler(answerId: number, isAnswered: boolean) {
        if (isAnswered)
            return undefined;

        return () => {
            this.answer(answerId);
        }
    }

    private leaveFeedback(feedback: Feedback) {
        const {progress} = this.props;

        playSound("button-click");

        this.props.leaveFeedback({
            questionId: progress.questionId,
            answerId: progress.answerId,
            feedback
        });
    }

    protected answer(answerId: number) {
        const {levelInProgress, question, answers, progress, answerQuestion} = this.props;
        const answer = answers[answerId];
        const attempts = (progress && progress.attempts) || 0;

        answerQuestion({
            levelId: levelInProgress,
            questionId: answer.questionId,
            answerId: answerId,
            isRight: answer.isRight,
            score: answer.isRight ? question.reputationPoints : 0,
            attempts: answer.isRight ? -1 : attempts - 1,
            timestamp: new Date().getTime()
        });
    }

    protected async share() {
        const {question, answers, shareImageApiEndpoint, shareImageTemplate} = this.props;

        let avatar = undefined;
        if (this.props.avatar) {
            try {
                avatar = await toDataURL(this.props.avatar);
            } catch (error) {
                this.props.trackError({
                    logLevel: LogLevel.Warn,
                    category: "QuestionScene",
                    message: "Failed to fetch user avatar",
                    error: error,
                    data: {
                        avatar: this.props.avatar
                    }
                });
            }
        }

        let questionImage = question.imageUrl;
        if (questionImage) {
            try {
                questionImage = await toDataURL(questionImage);
            }
            catch (error) {
                this.props.trackError({
                    logLevel: LogLevel.Warn,
                    category: "QuestionScene",
                    message: "Failed to convert question image to data uri",
                    error: error,
                    data: {
                        imageUrl: question.imageUrl
                    }
                });
            }
        }

        try {
            const shareImage = await getShareImage(shareImageApiEndpoint, shareImageTemplate, {
                title: question.title,
                imageUrl: questionImage,
                answers: question.answers.map(answerId => answers[answerId].text),
                avatar: avatar
            });

            await SDK.instance.share({
                image: shareImage,
                text: interpolate(i18n("questionanswer.sharequestiontext"), { questionText: question.title }),
                data: {
                    questionId: question.id
                },
                tracking: this.props.shareTracking
            });

            this.props.trackAction({
                category: "question",
                name: "share",
            });
        } catch (error) {
            this.props.trackError({
                category: "QuestionScene",
                message: "Failed to share question",
                error: error,
                data: {
                    questionId: question.id,
                }
            });
        }
    }

    protected continue() {
        const {leaveQuestion, progress} = this.props;

        playSound("button-click");

        leaveQuestion(progress);
    }
}